import * as React from 'react'
import classNames from 'classnames'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from './ExecutionElement'
import { ElementLabel } from './ElementLabel'
import { ElementValidation } from './ElementValidation'
import type * as Flow from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import { SVG_Delete } from '../svg/SVG_Delete'
import { SVG_Resume } from '../svg/SVG_Resume'
import { SVG_Info } from '../svg/SVG_Info'
import { type PromiseState, initialPromiseState } from '../util/PromiseState'
import { createPromiseManager, cancelPromiseManagers } from '../util/PromiseManager'
import { createStateLens } from '../util/react'
import type { FlowReportStatus } from './FlowReport'
import i18next, { type TFunction } from 'i18next'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import type { FeatureFlag } from '../../../shared/domain/status/types.no-deps'
import { Warning } from '../ui/Warning'
import { isRestrictedUser } from '../../../shared/domain/users/permissions'

interface Props {
    deps: CommonDeps
    elementId: Flow.ElementId
    procedureId: number
    loopPathIds: LoopPathIds
    elementLabel: string
    validationMessages: string[]
    disableEdit?: boolean
    required: boolean
    minReports?: number
    maxReports?: number
    subtaskReports: Map<Flow.ReportId, SubtaskReport>
    createSubtask: () => Promise<Flow.ReportId>
    deleteSubtask: (reportId: Flow.ReportId) => Promise<void>
    routeToSubtask: (reportId: Flow.ReportId) => void
    createAnswer: CreateAnswer<Flow.ReportsAnswer>
    answerValue: Flow.ReportsAnswer['value']
    isWorkflowDeleted?: boolean
    isGuide?: boolean
    openOptions?: () => void
    featureEnabled: (featureFlag: FeatureFlag) => boolean
    hasAccessToProcedure: (procedureId: number) => Promise<boolean>
}

export type SubtaskReport = {
    id: Flow.ReportId
    startDate: Date
    status: FlowReportStatus
    reportTitle?: string
    procedureName: string
    workflowName: string
    subjectUnitName?: string
}

type State = {
    loadingState: PromiseState<void>
    deletingSubtaskId: Flow.ReportId
    hasAccessToSubtask: boolean
}

export class ElementSubtask extends React.Component<Props, State> {
    public state: State = {
        loadingState: initialPromiseState,
        deletingSubtaskId: null,
        hasAccessToSubtask: true,
    }

    private promiseManagers = {
        loading: createPromiseManager(() => this.props.deps, createStateLens(this, 'loadingState')),
    }

    componentDidMount() {
        this.hasAccessToProcedureInSubtask().then((status) => this.setState({ hasAccessToSubtask: status }))
    }

    public componentWillUnmount = () => {
        cancelPromiseManagers(this.promiseManagers)
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private startFlow = () =>
        this.promiseManagers.loading.trigger(() =>
            this.props
                .createSubtask()
                .then((id) => this.createAnswer(id).then(() => id))
                .then(this.props.routeToSubtask)
        )

    private createAnswer = (reportId: Flow.ReportId) =>
        this.props.createAnswer((base) => ({
            ...base,
            type: 'reports',
            value: [...(this.props.answerValue ?? []), reportId],
        }))

    private startDeletingSubtask = async (reportId: Flow.ReportId) => this.setState({ deletingSubtaskId: reportId })
    private clearDeletingSubtask = () => this.setState({ deletingSubtaskId: null })

    private deleteSubtask = (reportId: Flow.ReportId) =>
        Promise.all([
            this.props.createAnswer((base) => ({
                ...base,
                type: 'reports',
                value: this.props.answerValue.filter((id) => id !== reportId),
            })),
            this.props.deleteSubtask(reportId),
        ]).then(this.clearDeletingSubtask)

    private hasAccessToProcedureInSubtask = (): Promise<boolean> => {
        if (!isRestrictedUser(this.props.deps.user?.privs)) {
            return Promise.resolve(true)
        }

        return this.props.hasAccessToProcedure(this.props.procedureId)
    }

    private tryRender = () => {
        const {
            elementId,
            elementLabel,
            validationMessages,
            disableEdit = false,
            required,
            minReports,
            maxReports,
            subtaskReports,
            routeToSubtask,
            answerValue = [],
            deps,
            isGuide = false,
        } = this.props
        const loading = this.promiseManagers.loading.isInProgress()
        const isRequired = required

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
            >
                <ElementLabel
                    required={required}
                    label={labelText(minReports, maxReports, isRequired)}
                    error={isGuide}
                    validationMessages={[]}
                    openOptions={this.props.openOptions}
                />
                {!this.state.hasAccessToSubtask && (
                    <div className="AppCore__flow__ElementSubtask__noAccessSubtask_warning">
                        <Warning message={noAccessToSubTaskMessage(i18next.t)} />
                    </div>
                )}
                <StartTaskButton
                    subTaskLabel={elementLabel}
                    onClick={this.startFlow}
                    disabled={
                        isGuide ||
                        disableEdit ||
                        loading ||
                        (maxReports && answerValue.length >= maxReports) ||
                        this.props.isWorkflowDeleted ||
                        !this.state.hasAccessToSubtask
                    }
                    showSpinner={loading}
                />
                {isGuide && GuideSubtaskMessage(required)}
                {answerValue.map((reportId) => (
                    <SubTaskReport
                        key={reportId}
                        subtaskReport={subtaskReports.get(reportId)}
                        deps={deps}
                        onDelete={this.startDeletingSubtask}
                        onClick={routeToSubtask}
                    />
                ))}
                <div className="AppCore__flow__ElementSubtask__validation">
                    <ElementValidation validationMessages={validationMessages} />
                </div>
                {this.state.deletingSubtaskId != null && (
                    <DeleteModal
                        onDone={() => this.deleteSubtask(this.state.deletingSubtaskId)}
                        onCancel={this.clearDeletingSubtask}
                    />
                )}
            </ExecutionElement>
        )
    }
}

function DeleteModal({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
    return (
        <zaptic-modal>
            <zaptic-full-page-message heading={i18next.t('appcore:element.subtask.deleteReport')}>
                <div className="AppCore__flow__ElementSubtask__report--deletion-text">
                    {i18next.t('appcore:element.subtask.promptDelete')}
                </div>
                <zaptic-spacer vertical-spacing={30}></zaptic-spacer>

                <zaptic-popup-button color="red" onClick={onDone}>
                    {i18next.t('appcore:element.subtask.confirmDelete')}
                </zaptic-popup-button>

                <zaptic-popup-button color="white" onClick={onCancel}>
                    {i18next.t('appcore:common.cancel')}
                </zaptic-popup-button>
            </zaptic-full-page-message>
        </zaptic-modal>
    )
}

function StartTaskButton({
    subTaskLabel,
    showSpinner,
    ...rest
}: {
    subTaskLabel: string
    showSpinner: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button className={'AppCore__flow__ElementSubtask__button'} {...rest}>
            {showSpinner ? <zaptic-spinner></zaptic-spinner> : subTaskLabel}
        </button>
    )
}

function SubTaskReport({
    subtaskReport,
    deps,
    onDelete,
    onClick,
}: {
    subtaskReport: SubtaskReport
    deps: CommonDeps
    onDelete: (reportId: Flow.ReportId) => Promise<void>
    onClick: (reportId: Flow.ReportId) => void
}) {
    if (subtaskReport == null) {
        return null
    }
    const inProgress = subtaskReport.status === 'open'
    return (
        <div
            className={classNames('AppCore__flow__ElementSubtask__report', {
                inProgress,
            })}
            id={`ElementSubtask__report__${subtaskReport.id}`}
        >
            <div
                className={'AppCore__flow__ElementSubtask__report--content'}
                onClick={() => onClick(subtaskReport.id)}
                tabIndex={0}
                role="link"
            >
                <div className={'AppCore__flow__ElementSubtask__report--title'}>
                    <b>{subtaskReport.reportTitle ?? subtaskReport.workflowName}</b>
                </div>
                <div>
                    <b>{i18next.t('appcore:element.subtask.procedure')}</b>
                    {subtaskReport.procedureName}
                </div>
                {subtaskReport.subjectUnitName && (
                    <div>
                        <b>{i18next.t('appcore:element.subtask.subject')}</b>
                        {subtaskReport.subjectUnitName}
                    </div>
                )}
                <div className={'AppCore__flow__ElementSubtask__report--startDate'}>
                    <zaptic-relative-time
                        time={subtaskReport.startDate.toISOString()}
                        locale={deps.locale}
                    ></zaptic-relative-time>
                </div>
            </div>
            <div className={'AppCore__flow__ElementSubtask__report--actions'}>
                <button onClick={() => onDelete(subtaskReport.id)} className="AppCore__flow__ElementSubtask__delete">
                    <SVG_Delete />
                </button>
                {inProgress && (
                    <button onClick={() => onClick(subtaskReport.id)} role="link">
                        <SVG_Resume />
                    </button>
                )}
            </div>
        </div>
    )
}

function labelText(minReports: number, maxReports: number, required: boolean) {
    const hasMinimum = minReports > 0
    // Max reports == 1 is a single select which is a different state to what
    // we are checking for with this variable. We explicitly want multiselects
    // with a maximum value.
    const hasMaximum = maxReports > 1

    if (required && hasMaximum && !hasMinimum) {
        return i18next.t('appcore:element.subtask.requiredMax', { maxReports })
    }

    if (required && !hasMaximum && hasMinimum) {
        return i18next.t('appcore:element.subtask.requiredMin', { minReports })
    }

    if (required && hasMaximum && hasMinimum) {
        return i18next.t('appcore:element.subtask.requiredMinMax', { minReports, maxReports })
    }

    if (required) {
        return i18next.t('appcore:element.subtask.mandatoryField', { minReports, maxReports })
    }
    // required === false at this point so we no longer have to check it

    if (hasMaximum && !hasMinimum) {
        return i18next.t('appcore:element.subtask.maxNoMin', { maxReports })
    }

    if (!hasMaximum && hasMinimum) {
        return i18next.t('appcore:element.subtask.minNoMax', { maxReports })
    }

    if (hasMaximum && hasMinimum) {
        return i18next.t('appcore:element.subtask.minMax', { minReports, maxReports })
    }

    // I would love a way to assert that all cases have been exhaustively
    // covered here but I have been smashing my head against it for a good while
    // and have concluded that it is impossible in typescript as there are no
    // tuples and the compiller can't determine that all cases are covered in
    // non trival branchs (ie combinations of a && b)
    return ''
}

function GuideSubtaskMessage(isRequired: boolean) {
    const errorMessage = isRequired
        ? i18next.t('appcore:element.subtask.requiredGuideError')
        : i18next.t('appcore:element.subtask.guideError')

    return (
        <div className="AppCore__flow__ElementSubtask__guide--wrapper">
            <div>
                <SVG_Info color="var(--red)" />
            </div>
            <div className="AppCore__flow__ElementSubtask__guide--error">{errorMessage}</div>
        </div>
    )
}

const noAccessToSubTaskMessage = (i18n: TFunction) => i18n('appcore:element.subtask.noAccess')
